import { getCsrfToken } from '../utils/csrf';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'auctioneer';
  isActive?: boolean;
  lastLogin?: Date;
  createdAt?: Date;
  limits?: {
    maxPlayers: number;
    maxTeams: number;
  };
  usage?: {
    totalPlayers: number;
    totalTeams: number;
  };
  accessExpiry?: string;
}

export interface AuthResponse {
  success: boolean;
  data?: {
    user: User;
  };
  error?: string;
}

class AuthService {
  // Fetch CSRF token from server (sets csrf-token cookie)
  async fetchCsrfToken(): Promise<void> {
    try {
      await fetch(`${API_URL}/auth/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
    }
  }

  // Register new user
  async register(name: string, email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name, email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Store user data for display purposes
      if (data.data?.user) {
        localStorage.setItem('user', JSON.stringify(data.data.user));
      }

      return data;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Registration failed',
      };
    }
  }

  // Login user
  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store user data for display purposes
      if (data.data?.user) {
        localStorage.setItem('user', JSON.stringify(data.data.user));
      }

      return data;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Login failed',
      };
    }
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      const csrfToken = getCsrfToken();
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
        credentials: 'include',
      });

      localStorage.removeItem('user');
    } catch (error) {
      // Always clear user even if request fails
      localStorage.removeItem('user');
    }
  }

  // Get current user
  async getMe(): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch user');
      }

      return data;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch user',
      };
    }
  }

  // Update user details
  async updateDetails(name: string, email: string): Promise<AuthResponse> {
    try {
      const csrfToken = getCsrfToken();
      const response = await fetch(`${API_URL}/auth/updatedetails`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ name, email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Update failed');
      }

      return data;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Update failed',
      };
    }
  }

  // Update password
  async updatePassword(currentPassword: string, newPassword: string): Promise<AuthResponse> {
    try {
      const csrfToken = getCsrfToken();
      const response = await fetch(`${API_URL}/auth/updatepassword`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password update failed');
      }

      return data;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Password update failed',
      };
    }
  }

  // Check if user is authenticated (based on cached user object)
  isAuthenticated(): boolean {
    return !!localStorage.getItem('user');
  }
}

const authServiceInstance = new AuthService();
export default authServiceInstance;
